import { pointInTriangle2D, segmentsIntersect } from '@data/geometry/Functions.js';
import type FVector from '@data/geometry/FVector.js';
import * as THREE from 'three';
import { earcut } from 'Utils/Earcut';
import { getViewer } from 'store/viewerStore';
import type { TargetPolygon, Targets } from './Targets.js';

// =====================================
// TYPES
// =====================================

export type GroundTargetTexturesDescriptor = {
    triangleABTexture: THREE.DataTexture;
    triangleCKindTexture: THREE.DataTexture;
    cellSpanTexture: THREE.DataTexture;
    cellTriangleTexture: THREE.DataTexture;
    worldMin: THREE.Vector2;
    worldSize: THREE.Vector2;
    gridSize: number;
    maxTrianglesPerCell: number;
};

type ProjectedPoint = {
    x: number;
    y: number;
};

// Builds and stores GPU-friendly textures containing targets projected into ground space.
// 1) `triangleABTexture`: A/B vertex XY coordinates (local to worldMin).
// 2) `triangleCKindTexture`: C vertex XY coordinates (local to worldMin) + target kind.
// 3) `cellSpanTexture` + `cellTriangleTexture`: adaptive-grid candidate lookup.
export class GroundTargetTextures {
    // =====================================
    // CLASS FIELDS
    // =====================================

    // P.S. This could be further optimized by implementing a proper QuadTree instead of a fix-size grid.
    private static readonly BASE_GRID_SIZE = 128;
    private static readonly DEFAULT_MAX_GRID_SIZE = 4096;
    private static readonly MAX_TRIANGLES_PER_CELL = 96;
    private descriptor: GroundTargetTexturesDescriptor | null = null;
    private maxGridSize = GroundTargetTextures.DEFAULT_MAX_GRID_SIZE;

    // =====================================
    // LIFECYCLE
    // =====================================

    // Releases all allocated resources for this textures instance.
    dispose() {
        this.clear();
    }

    // Disposes active textures and resets the cached descriptor.
    clear() {
        this.descriptor?.triangleABTexture.dispose();
        this.descriptor?.triangleCKindTexture.dispose();
        this.descriptor?.cellSpanTexture.dispose();
        this.descriptor?.cellTriangleTexture.dispose();
        this.descriptor = null;
    }

    // Returns the currently prepared textures descriptor, if available.
    getDescriptor(): GroundTargetTexturesDescriptor | null {
        return this.descriptor;
    }

    // =========================================
    // MAIN LOGIC TO BUILD THE TARGET TEXTURES
    // =========================================

    // 1) Validates inputs and resets previous textures.
    // 2) Builds projected bounds from ground points.
    // 3) Collects all target triangles.
    // 4) Uploads triangle + adaptive lookup textures.
    setTargets(targets: Targets | null | undefined, groundPoints: FVector[] | undefined) {
        // 0) Assign maxGridSize based on the renderer capabilities:
        const maxTextureSize = getViewer()?.renderer?.capabilities?.maxTextureSize;
        this.maxGridSize = Number.isFinite(maxTextureSize)
            ? Math.max(GroundTargetTextures.BASE_GRID_SIZE, Math.floor(maxTextureSize as number))
            : (this.maxGridSize = GroundTargetTextures.DEFAULT_MAX_GRID_SIZE);

        // Validate inputs:
        if (!targets || targets.targets.length === 0 || !groundPoints || groundPoints.length === 0) {
            return;
        }

        this.clear();

        const { min, size } = this.computeProjectedBounds(groundPoints);
        if (size.x <= 0 || size.y <= 0) {
            return;
        }

        const triangles: Array<{ ax: number; ay: number; bx: number; by: number; cx: number; cy: number; kind: number }> = [];

        // 1) Collect triangles for target polygons:
        for (const target of targets.targets) {
            for (const polygon of target.polygons) {
                this.collectPolygonTriangles(triangles, polygon, target.kind);
            }
        }

        if (triangles.length === 0) {
            return;
        }

        // 2) Build textures for collected triangles and their adaptive grid lookup:
        const triangleABTexture = this.buildTriangleVertexABTexture(triangles, min);
        const triangleCKindTexture = this.buildTriangleVertexCAndKindTexture(triangles, min);
        const { cellSpanTexture, cellTriangleTexture, maxTrianglesPerCell, gridSize } = this.buildGridCellTriangleIndexTexture(
            triangles,
            min,
            size
        );

        this.descriptor = {
            triangleABTexture,
            triangleCKindTexture,
            cellSpanTexture,
            cellTriangleTexture,
            worldMin: min,
            worldSize: size,
            gridSize,
            maxTrianglesPerCell,
        };
    }

    // Computes projected 2D world bounds for a set of ground points.
    private computeProjectedBounds(points: FVector[]): { min: THREE.Vector2; size: THREE.Vector2 } {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const point of points) {
            const projected = this.projectPoint(point);
            minX = Math.min(minX, projected.x);
            minY = Math.min(minY, projected.y);
            maxX = Math.max(maxX, projected.x);
            maxY = Math.max(maxY, projected.y);
        }

        const min = new THREE.Vector2(minX, minY);
        const size = new THREE.Vector2(Math.max(maxX - minX, 1), Math.max(maxY - minY, 1));
        return { min, size };
    }

    // Collects triangles for a given target polygon, either from explicit indices (V16+) or via fallback triangulation (V15-).
    private collectPolygonTriangles(
        out: Array<{ ax: number; ay: number; bx: number; by: number; cx: number; cy: number; kind: number }>,
        polygon: TargetPolygon,
        kind: number
    ) {
        if (polygon.vertices.length === 0 && polygon.points.length === 0) {
            return;
        }

        // 1) Target V16+: payload has explicit target triangle indices:
        if (polygon.indices.length >= 3 && polygon.vertices.length > 0) {
            const projectedVertices = polygon.vertices.map((vertex) => this.projectPoint(vertex));
            for (let i = 0; i < polygon.indices.length; i += 3) {
                // 1.1) Read triangle vertex indices:
                const ia = polygon.indices[i];
                const ib = polygon.indices[i + 1];
                const ic = polygon.indices[i + 2];

                // 1.2) Get triangle vertices; skip degenerate triangles:
                const a = projectedVertices[ia];
                const b = projectedVertices[ib];
                const c = projectedVertices[ic];
                if (!a || !b || !c) continue;

                this.addTriangle(out, a, b, c, kind);
            }
            return;
        }

        // B) Target V15-: payload doesn't have explicit target triangle indices, make an "on the fly" triangulation (earcut),
        // using the polygon "points" which are nothing other than the ground vertices used to generate the target:
        const fallbackPoints = (polygon.points.length >= 3 ? polygon.points : polygon.vertices).map((point) => this.projectPoint(point));
        if (fallbackPoints.length < 3) return;

        const cleaned = this.stripDuplicateClosingPoint(fallbackPoints);
        if (cleaned.length < 3) return;

        const flat: number[] = [];
        for (const point of cleaned) {
            flat.push(point.x, point.y);
        }

        // Earcut triangulation:
        const indices = earcut(flat, undefined, 2);
        for (let i = 0; i < indices.length; i += 3) {
            const a = cleaned[indices[i]];
            const b = cleaned[indices[i + 1]];
            const c = cleaned[indices[i + 2]];
            if (!a || !b || !c) continue;
            this.addTriangle(out, a, b, c, kind);
        }
    }

    private stripDuplicateClosingPoint(points: ProjectedPoint[]): ProjectedPoint[] {
        if (points.length < 2) return points;
        const first = points[0];
        const last = points[points.length - 1];
        if (Math.abs(first.x - last.x) < 1e-8 && Math.abs(first.y - last.y) < 1e-8) {
            return points.slice(0, -1);
        }
        return points;
    }

    // Projects a point into textures space (Y axis inverted):
    private projectPoint(point: { x: number; y: number }): ProjectedPoint {
        return {
            x: point.x,
            y: -point.y,
        };
    }

    // Adds a non-degenerate projected triangle to the output buffer.
    private addTriangle(
        out: Array<{ ax: number; ay: number; bx: number; by: number; cx: number; cy: number; kind: number }>,
        a: ProjectedPoint,
        b: ProjectedPoint,
        c: ProjectedPoint,
        kind: number
    ) {
        const area = this.edgeFunction(a, b, c);
        if (Math.abs(area) < 1e-8) return;
        out.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, cx: c.x, cy: c.y, kind });
    }

    // Computes signed area contribution used for winding/degeneracy checks.
    private edgeFunction(a: ProjectedPoint, b: ProjectedPoint, p: ProjectedPoint): number {
        return (p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x);
    }

    // =====================================
    // TEXTURES BUILDING
    // =====================================

    // Given the data, sizes and format, creates a THREE Data Texture:
    private createDataTextureFromFloatBuffer(
        data: Float32Array,
        width: number,
        height: number,
        format: THREE.PixelFormat
    ): THREE.DataTexture {
        const textureData = new Float32Array(data.length);
        textureData.set(data);
        const texture = new THREE.DataTexture(textureData, width, height, format, THREE.FloatType);
        texture.needsUpdate = true;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.generateMipmaps = false;
        texture.flipY = false;
        texture.unpackAlignment = 1;
        return texture;
    }

    // 1st Texture: Packs 2D triangle A/B coordinates into a float texture:
    private buildTriangleVertexABTexture(
        triangles: Array<{ ax: number; ay: number; bx: number; by: number; cx: number; cy: number; kind: number }>,
        worldMin: THREE.Vector2
    ): THREE.DataTexture {
        const count = triangles.length;
        const width = Math.max(1, Math.ceil(Math.sqrt(count)));
        const height = Math.max(1, Math.ceil(count / width));
        const data = new Float32Array(width * height * 4);
        for (let i = 0; i < count; i++) {
            const base = i * 4;
            const tri = triangles[i];
            data[base] = tri.ax - worldMin.x;
            data[base + 1] = tri.ay - worldMin.y;
            data[base + 2] = tri.bx - worldMin.x;
            data[base + 3] = tri.by - worldMin.y;
        }
        return this.createDataTextureFromFloatBuffer(data, width, height, THREE.RGBAFormat);
    }

    // 2nd Texture: Packs 2D triangle C coordinates and Target Kind into a float texture:
    private buildTriangleVertexCAndKindTexture(
        triangles: Array<{ ax: number; ay: number; bx: number; by: number; cx: number; cy: number; kind: number }>,
        worldMin: THREE.Vector2
    ): THREE.DataTexture {
        const count = triangles.length;
        const width = Math.max(1, Math.ceil(Math.sqrt(count)));
        const height = Math.max(1, Math.ceil(count / width));
        const data = new Float32Array(width * height * 4);
        for (let i = 0; i < count; i++) {
            const base = i * 4;
            const tri = triangles[i];
            data[base] = tri.cx - worldMin.x;
            data[base + 1] = tri.cy - worldMin.y;
            data[base + 2] = tri.kind;
        }
        return this.createDataTextureFromFloatBuffer(data, width, height, THREE.RGBAFormat);
    }

    // 3rd & 4th Textures:
    // 1) Build an adaptive grid that keeps a safe triangle count per cell (count <= shader cap --> 512).
    // 2) Store per-cell span `(start,count)` in `cellSpanTexture` (refernces Triangles inside of the next texture).
    // 3) Store all candidate triangle indices in `cellTriangleTexture`.
    private buildGridCellTriangleIndexTexture(
        triangles: Array<{ ax: number; ay: number; bx: number; by: number; cx: number; cy: number; kind: number }>,
        worldMin: THREE.Vector2,
        worldSize: THREE.Vector2
    ): { cellSpanTexture: THREE.DataTexture; cellTriangleTexture: THREE.DataTexture; maxTrianglesPerCell: number; gridSize: number } {
        // 1) Prepare base values for the grid, and build it:
        let gridSize = GroundTargetTextures.BASE_GRID_SIZE;
        let cellTriangleRefs: number[][] = [];
        let maxCellCount = 0;

        while (true) {
            const cellCount = gridSize * gridSize;
            cellTriangleRefs = Array.from({ length: cellCount }, () => []);
            maxCellCount = 0;
            const cellWidth = worldSize.x / gridSize;
            const cellHeight = worldSize.y / gridSize;

            // 1.1) Loop through every triangle:
            for (let triIndex = 0; triIndex < triangles.length; triIndex++) {
                const tri = triangles[triIndex];
                const minX = Math.min(tri.ax, tri.bx, tri.cx);
                const maxX = Math.max(tri.ax, tri.bx, tri.cx);
                const minY = Math.min(tri.ay, tri.by, tri.cy);
                const maxY = Math.max(tri.ay, tri.by, tri.cy);

                const minCellX = Math.max(0, Math.floor(((minX - worldMin.x) / worldSize.x) * gridSize));
                const maxCellX = Math.min(gridSize - 1, Math.floor(((maxX - worldMin.x) / worldSize.x) * gridSize));
                const minCellY = Math.max(0, Math.floor(((minY - worldMin.y) / worldSize.y) * gridSize));
                const maxCellY = Math.min(gridSize - 1, Math.floor(((maxY - worldMin.y) / worldSize.y) * gridSize));

                // 1.2) Push current triangle index in a cell (array) & increase maxCellCount if cell size is bigger than current max:
                for (let cellY = minCellY; cellY <= maxCellY; cellY++) {
                    for (let cellX = minCellX; cellX <= maxCellX; cellX++) {
                        // Cell min and max in World space, not grid space; used for triangle-cell overlap test:
                        const cellMinX = worldMin.x + cellX * cellWidth;
                        const cellMinY = worldMin.y + cellY * cellHeight;
                        const cellMaxX = cellMinX + cellWidth;
                        const cellMaxY = cellMinY + cellHeight;

                        // Triangle gets pushed IF its bounding box touches the cell AND triangle-cell overlap test passes:
                        if (!this.triangleIntersectsRect(tri, cellMinX, cellMinY, cellMaxX, cellMaxY)) {
                            continue;
                        }

                        const cellIndex = cellY * gridSize + cellX;
                        const refs = cellTriangleRefs[cellIndex];
                        refs.push(triIndex);
                        if (refs.length > maxCellCount) maxCellCount = refs.length;
                    }
                }
            }

            // 1.3) Stop increasing the grid size if:
            //      a) Max triangles in any cell is lower than MAX_TRIANGLES_PER_CELL (no cell overflows, we're good to go).
            //      b) Grid reaches MAX_GRID_SIZE.
            if (maxCellCount <= GroundTargetTextures.MAX_TRIANGLES_PER_CELL || gridSize >= this.maxGridSize) {
                break;
            }
            gridSize *= 2;
        }

        // 2) Pack variable-length per-cell lists into `(start,count)` + flat index buffer:
        const cellCount = gridSize * gridSize;
        const cellSpanData = new Float32Array(cellCount * 2);
        const flatTriangleRefs: number[] = [];
        let cursor = 0;

        for (let cellIndex = 0; cellIndex < cellCount; cellIndex++) {
            const refs = cellTriangleRefs[cellIndex];
            cellSpanData[cellIndex * 2] = cursor;
            cellSpanData[cellIndex * 2 + 1] = refs.length;
            for (let i = 0; i < refs.length; i++) {
                flatTriangleRefs.push(refs[i]);
            }
            cursor += refs.length;
        }

        const cellSpanTexture = this.createDataTextureFromFloatBuffer(cellSpanData, gridSize, gridSize, THREE.RGFormat);

        // 3) Upload flattened triangle refs as R32F; shader fetches by linear index:
        const flatCount = Math.max(flatTriangleRefs.length, 1);
        const width = Math.max(1, Math.ceil(Math.sqrt(flatCount)));
        const height = Math.max(1, Math.ceil(flatCount / width));
        const flatData = new Float32Array(width * height).fill(-1);
        for (let i = 0; i < flatTriangleRefs.length; i++) {
            flatData[i] = flatTriangleRefs[i];
        }
        const cellTriangleTexture = this.createDataTextureFromFloatBuffer(flatData, width, height, THREE.RedFormat);
        (cellTriangleTexture as any).internalFormat = 'R32F';

        return {
            cellSpanTexture, // Stores (start, count) to access triangle ids in TriangleTex
            cellTriangleTexture, // Stores actual triangle ids
            maxTrianglesPerCell: Math.min(maxCellCount, GroundTargetTextures.MAX_TRIANGLES_PER_CELL),
            gridSize,
        };
    }

    // Check if a triangle is inside a cell using a conservative + exact-enough overlap test, to speed up shader candidate selection:
    private triangleIntersectsRect(
        tri: { ax: number; ay: number; bx: number; by: number; cx: number; cy: number },
        minX: number,
        minY: number,
        maxX: number,
        maxY: number
    ): boolean {
        const a = new THREE.Vector2(tri.ax, tri.ay);
        const b = new THREE.Vector2(tri.bx, tri.by);
        const c = new THREE.Vector2(tri.cx, tri.cy);

        const triMinX = Math.min(a.x, b.x, c.x);
        const triMaxX = Math.max(a.x, b.x, c.x);
        const triMinY = Math.min(a.y, b.y, c.y);
        const triMaxY = Math.max(a.y, b.y, c.y);
        if (triMaxX < minX || triMinX > maxX || triMaxY < minY || triMinY > maxY) {
            return false;
        }

        const rect: THREE.Vector2[] = [
            new THREE.Vector2(minX, minY),
            new THREE.Vector2(maxX, minY),
            new THREE.Vector2(maxX, maxY),
            new THREE.Vector2(minX, maxY),
        ];

        if (
            pointInTriangle2D(rect[0], a, b, c) ||
            pointInTriangle2D(rect[1], a, b, c) ||
            pointInTriangle2D(rect[2], a, b, c) ||
            pointInTriangle2D(rect[3], a, b, c)
        ) {
            return true;
        }

        if (
            this.pointInRect(a, minX, minY, maxX, maxY) ||
            this.pointInRect(b, minX, minY, maxX, maxY) ||
            this.pointInRect(c, minX, minY, maxX, maxY)
        ) {
            return true;
        }

        const triEdges: Array<[THREE.Vector2, THREE.Vector2]> = [
            [a, b],
            [b, c],
            [c, a],
        ];
        const rectEdges: Array<[THREE.Vector2, THREE.Vector2]> = [
            [rect[0], rect[1]],
            [rect[1], rect[2]],
            [rect[2], rect[3]],
            [rect[3], rect[0]],
        ];

        for (const [t0, t1] of triEdges) {
            for (const [r0, r1] of rectEdges) {
                if (segmentsIntersect(t0, t1, r0, r1)) {
                    return true;
                }
            }
        }

        return false;
    }

    private pointInRect(p: THREE.Vector2, minX: number, minY: number, maxX: number, maxY: number): boolean {
        return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
    }
}
